import { useLayoutEffect } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  ISurveyAnswerItem,
  ISurveyQuestion,
  ISurveyQuestionItemGroupsQuestion,
} from "../libs/interfaces/services";
import { QuestionType } from "../models/QuestionTypeEnum";

/**
 * Hook que encapsula toda a lógica de schema Yup, valores padrão e form do react-hook-form
 * para uma questão específica dentro de um tópico.
 */
export function useQuestionForm(
  surveyTopic: ISurveyQuestion,
  indexQuiz: number,
  answerList: ISurveyAnswerItem[],
  surveyQuizIndex: number
) {
  const createValidateSchema = () => {
    if (!surveyTopic?.questions[indexQuiz]?.groupsQuestionItens?.length) {
      return {};
    }

    let schema = {};
    const schemaArr = surveyTopic.questions[indexQuiz].groupsQuestionItens.map(
      (item) => {
        switch (item.questionType) {
          case QuestionType.Text:
            return item.required
              ? { [item.idSubQuestion]: yup.string().required() }
              : { [item.idSubQuestion]: yup.string() };

          case QuestionType.Int:
            return item.required
              ? {
                  [item.idSubQuestion]: yup
                    .number()
                    .integer()
                    .positive()
                    .required(),
                }
              : { [item.idSubQuestion]: yup.number().integer().positive() };

          case QuestionType.Date:
            return item.required
              ? { [item.idSubQuestion]: yup.date().required() }
              : {
                  [item.idSubQuestion]: yup
                    .date()
                    .nullable()
                    .notRequired()
                    .transform((value, original) =>
                      original === "" ? null : value
                    ),
                };

          case QuestionType.MultiSelect:
            return item.required
              ? {
                  [item.idSubQuestion]: yup
                    .array()
                    .of(yup.mixed())
                    .test((value) => !!value && value.some(Boolean))
                    .required(),
                }
              : { [item.idSubQuestion]: yup.array().of(yup.mixed()) };

          case QuestionType.Radio:
          case QuestionType.Scale:
            return item.required
              ? { [item.idSubQuestion]: yup.number().required() }
              : { [item.idSubQuestion]: yup.number() };

          default:
            return {};
        }
      }
    );

    schemaArr.forEach((schemaItem) => {
      schema = { ...schema, ...schemaItem };
    });

    return schema;
  };

  const createDefaultValues = () => {
    if (
      !answerList.length ||
      !surveyTopic?.questions[indexQuiz]?.groupsQuestionItens?.length
    ) {
      return {} as any;
    }

    let defaultValues = {};

    const defaultValuesArr = answerList.map((answer) => {
      const subQuestion = surveyTopic?.questions[
        indexQuiz
      ]?.groupsQuestionItens.find(
        (question) => question.idSubQuestion === answer.idSubQuestion
      );
      const type = subQuestion?.questionType;

      switch (type) {
        case QuestionType.Text:
          return { [answer.idSubQuestion]: answer.answerString };

        case QuestionType.Int:
          return { [answer.idSubQuestion]: answer.answerInt };

        case QuestionType.Date:
          if (answer.answerDate) {
            return {
              [answer.idSubQuestion]: new Date(answer.answerDate)
                .toISOString()
                .split("T")[0],
            };
          }
          return {};

        case QuestionType.MultiSelect: {
          // O CheckboxItem registra cada opção como `${idSubQuestion}[${index}]`,
          // indexado pela POSIÇÃO da opção em questionItens — não pelo idQuestionIten.
          // O backend armazena em answerArray a lista de idQuestionIten marcados.
          // Então, mapeamos cada idQuestionIten armazenado para a posição
          // correspondente em questionItens, preenchendo com false os não marcados.
          const options = subQuestion?.questionItens ?? [];
          let storedIds: (number | string)[] = [];
          try {
            const parsed = answer.answerArray
              ? JSON.parse(answer.answerArray)
              : [];
            if (Array.isArray(parsed)) storedIds = parsed;
          } catch {
            storedIds = [];
          }
          const storedSet = new Set(storedIds.map((id) => Number(id)));
          const positional = options.map((option) =>
            storedSet.has(Number(option.idQuestionIten))
              ? String(option.idQuestionIten)
              : false
          );
          return { [answer.idSubQuestion]: positional };
        }

        case QuestionType.Radio:
        case QuestionType.Scale:
          return { [answer.idSubQuestion]: answer.answerInt };

        default:
          return null;
      }
    });

    defaultValuesArr
      .filter((item) => item)
      .forEach((defaultValueItem) => {
        defaultValues = { ...defaultValues, ...defaultValueItem };
      });

    return defaultValues;
  };

  const schema = yup.object(createValidateSchema()).required();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: createDefaultValues(),
  });

  // Sincroniza o formulário quando o índice da questão muda
  useLayoutEffect(() => {
    const savedValues = createDefaultValues();

    // Quando há valores salvos, usa setValue para preencher campo a campo
    // sem desregistrar os inputs (diferente do reset que recria o estado).
    // Quando NÃO há valores salvos (primeira vez), apenas reseta com
    // valores vazios adequados para cada tipo — nunca undefined.
    const emptyValues: Record<string, any> = {};
    surveyTopic.questions[indexQuiz]?.groupsQuestionItens?.forEach((item) => {
      switch (item.questionType) {
        case QuestionType.MultiSelect:
          emptyValues[item.idSubQuestion] = [];
          break;
        case QuestionType.Int:
        case QuestionType.Radio:
        case QuestionType.Scale:
          emptyValues[item.idSubQuestion] = "";
          break;
        case QuestionType.Date:
          emptyValues[item.idSubQuestion] = "";
          break;
        default:
          emptyValues[item.idSubQuestion] = "";
          break;
      }
    });

    const mergedValues = { ...emptyValues, ...savedValues };

    // Usa setValue individual para não desregistrar campos já montados
    for (const key in mergedValues) {
      setValue(key, mergedValues[key]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyQuizIndex]);

  return {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    errors: errors as any,
  };
}
